import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { 
  CheckCircle2, 
  XCircle, 
  ArrowRight, 
  ChevronLeft,
  Info,
  Loader2,
  Settings2,
  AlertTriangle,
  X
} from "lucide-react";

interface Question {
  id: number;
  title: string;
  weight: number;
}

const Condition: React.FC = () => {
  const navigate = useNavigate();
  const { variantId, deviceId } = useParams();
  
  const [groupId, setGroupId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [showIneligibleModal, setShowIneligibleModal] = useState(false);

  useEffect(() => {
    if (variantId) {
      api.get(`/inspection/variant/${variantId}`)
        .then((res) => setGroupId(res.inspection_group_id))
        .catch(() => console.error("Failed to load group"));
    }
  }, [variantId]);

  useEffect(() => {
    if (!groupId) return;
    api.get(`/inspection/group/${groupId}/global`)
      .then((data) => setQuestions(data))
      .catch(() => console.error("Failed to load questions"))
      .finally(() => setLoading(false));
  }, [groupId]);

  const handleAnswer = (qId: number, value: boolean) => {
    if (value === false) {
      setShowIneligibleModal(true);
    }
    setAnswers((prev) => ({ ...prev, [qId]: value }));
  };

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;
  const hasNoAnswer = Object.values(answers).some((val) => val === false);
  const canContinue = questions.length > 0 && answeredCount === questions.length && !hasNoAnswer;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest italic text-center">Diagnostics in Progress...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32 relative">
      
      {/* --- PREMIUM FLASH MODAL --- */}
      {showIneligibleModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop with Blur */}
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setShowIneligibleModal(false)}
          />
          
          {/* Modal Content with Zoom-In Flash Effect */}
          <div className="relative bg-white w-full max-w-[450px] rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-300">
            <div className="absolute top-4 right-4">
              <button 
                onClick={() => setShowIneligibleModal(false)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-8 pt-10 flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mb-6 animate-bounce">
                <AlertTriangle className="text-rose-500" size={40} />
              </div>
              
              <h2 className="text-2xl font-black text-slate-900 leading-tight">
                Quality Check Failed
              </h2>
              
              <p className="text-slate-500 mt-4 text-sm font-medium leading-relaxed">
                As per our strict quality guidelines, we cannot purchase devices with this specific functional issue. Please ensure the device meets all conditions to proceed with the sale.
              </p>

              <div className="w-full mt-8 space-y-3">
                <button 
                  onClick={() => setShowIneligibleModal(false)}
                  className="w-full py-4 bg-[#363C90] text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-indigo-200 hover:scale-[1.02] transition-transform"
                >
                  I Understand
                </button>
                <button 
                  onClick={() => navigate('/')}
                  className="w-full py-3 text-slate-400 font-bold text-xs uppercase tracking-widest hover:text-slate-600"
                >
                  Go Back to Home
                </button>
              </div>
            </div>

            <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Sell30 Verification Protocol v2.4
              </p>
            </div>
          </div>
        </div>
      )}

      {/* --- STICKY PROGRESS BAR --- */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[1000px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400">
            <ChevronLeft size={20} />
          </button>
          
          <div className="flex-1 max-w-md">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-[10px] font-black text-[#363C90] uppercase tracking-wider">Device Health</span>
              <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-md border">
                {answeredCount}/{questions.length}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${hasNoAnswer ? 'bg-rose-500' : 'bg-emerald-500'}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100/50">
            <Info size={14} />
            <span className="text-[9px] font-black uppercase">Official Diagnostic</span>
          </div>
        </div>
      </div>

      <div className="max-w-[700px] mx-auto px-4 mt-10">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4 border border-indigo-100">
            <Settings2 className="text-[#363C90]" size={24} />
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">Functional Check</h1>
          <p className="text-slate-400 text-sm mt-1 font-medium italic">Accuracy ensures same-day payment.</p>
        </div>

        {/* --- QUESTIONS LIST --- */}
        <div className="space-y-4">
          {questions.map((q, idx) => (
            <div 
              key={q.id}
              className={`bg-white p-5 md:p-6 rounded-2xl border transition-all duration-300
                ${answers[q.id] === false ? "border-rose-500 bg-rose-50/5" : (answers[q.id] === true ? "border-emerald-500/30 scale-[1.01]" : "border-slate-100")}
              `}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <span className="text-[9px] font-black text-slate-400 uppercase bg-slate-50 px-2 py-0.5 rounded border">Q.{idx + 1}</span>
                  <h3 className="text-md md:text-lg font-bold text-slate-800 leading-tight mt-2">{q.title}</h3>
                  {answers[q.id] === false && (
                    <p className="text-rose-500 text-[10px] font-black mt-2 italic uppercase animate-pulse">Critical Fail</p>
                  )}
                </div>

                <div className="flex gap-2 w-full md:w-[200px]">
                  <button
                    onClick={() => handleAnswer(q.id, true)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-black text-xs transition-all uppercase
                      ${answers[q.id] === true ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "bg-slate-50 text-slate-400 border border-slate-100"}
                    `}
                  >
                    <CheckCircle2 size={14} /> Yes
                  </button>

                  <button
                    onClick={() => handleAnswer(q.id, false)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-black text-xs transition-all uppercase
                      ${answers[q.id] === false ? "bg-rose-500 text-white shadow-lg shadow-rose-500/20" : "bg-slate-50 text-slate-400 border border-slate-100"}
                    `}
                  >
                    <XCircle size={14} /> No
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* --- FOOTER --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-100 p-4 z-40">
        <div className="max-w-[700px] mx-auto">
          <button
            disabled={!canContinue}
            onClick={() => {
              navigate(`/sell/defects/${deviceId}/${variantId}`, { 
                state: { deviceId, variantId, groupId, globalAnswers: answers } 
              });
            }}
            className={`w-full py-4 rounded-xl font-black text-[13px] flex items-center justify-center gap-2 transition-all uppercase tracking-[2px]
              ${canContinue ? "bg-[#363C90] text-white hover:bg-[#2a2f70] shadow-xl shadow-indigo-900/20" : "bg-slate-100 text-slate-300 cursor-not-allowed"}
            `}
          >
            {hasNoAnswer ? "Ineligible to Proceed" : <>Physical Defects Next <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Condition;